import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Dialog } from '@/components/ui/dialog'
import { BottomSheetDialog } from '@/components/ui/bottom-sheet-dialog'
import CurrencyDollarCircleIcon from '@/assets/icons/currency-dollar-circle.svg'
import ArrowPrevious from '@/assets/icons/arrow-previous'
import ArrowNext from '@/assets/icons/arrow-next'
import PlaidTransactionPickerContent from './plaid-transaction-picker-content'
import PlaidTransactionPickerConfirmationModal from './plaid-transaction-picker-confirmation-modal'
import { Button, ToastMessage } from '@/components'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { submitLoanTransactionsForReview } from '@/api/transactions/loans'
import { toast } from 'sonner'

const PlaidTransactionPickerModal = ({ isOpen, onClose, plaidItemId, transactions }) => {
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationType, setConfirmationType] = useState(null) // 'skip' or 'submit'
  const [transactionsPerPage, setTransactionsPerPage] = useState(5)
  const isMobile = useIsMobile()
  const transactionsArray = Array.isArray(transactions) ? transactions : []
  const totalPages = Math.ceil(transactionsArray.length / transactionsPerPage)
  const startIndex = (currentPage - 1) * transactionsPerPage
  const endIndex = startIndex + transactionsPerPage
  const currentPageTransactions = transactionsArray.slice(startIndex, endIndex)

  // Calculate and set initial estimate of rows per page when modal opens
  useEffect(() => {
    if (isOpen) {
      // Calculate initial estimate based on viewport dimensions
      const viewportHeight = window.innerHeight
      const modalHeight = isMobile ? viewportHeight * 0.9 : viewportHeight * 0.85
      const headerHeight = 120 // Approximate header height
      const footerHeight = 80 // Approximate footer height
      const padding = 50 // Approximate padding
      const availableHeight = modalHeight - headerHeight - footerHeight - padding
      
      // Estimate row height (typically 50-60px per row)
      const estimatedRowHeight = 60
      const estimatedRows = Math.floor(availableHeight / estimatedRowHeight)
      
      // Minimum 5 rows, maximum 20
      const initialRows = Math.max(5, Math.min(20, estimatedRows))
      setTransactionsPerPage(initialRows)
    }
  }, [isOpen, isMobile])

  // Reset selections and page when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedTransactionIds([])
      setCurrentPage(1)
      setShowConfirmationModal(false)
      setConfirmationType(null)
      setTransactionsPerPage(5) // Reset to default so it recalculates on next open
    }
  }, [isOpen])

  // Reset to page 1 when transactions change
  useEffect(() => {
    setCurrentPage(1)
  }, [transactionsArray.length])

  const handleCheckboxChange = (transactionId, checked) => {
    setSelectedTransactionIds(prev =>
      checked ? [...prev, transactionId] : prev.filter(id => id !== transactionId)
    )
  }

  const handleSubmit = async () => {
    if (selectedTransactionIds.length > 0) {
      const selectedTransactions = transactionsArray.filter(txn =>
        selectedTransactionIds.includes(txn.transaction_id)
      )
      
      try {
        const transactionIds = selectedTransactions.map(txn => txn.transaction_id)
        await submitLoanTransactionsForReview({
          plaidItemId,
          transactionIds,
        })
        
        // Only show confirmation modal on success
        setConfirmationType('submit')
        setShowConfirmationModal(true)
      } catch (error) {
        console.error('Error submitting transactions:', error)
        toast.custom(() => (
          <ToastMessage
            text="Failed to submit transactions"
            description="There was an error submitting your transactions. Please try again."
          />
        ))
        // Don't show confirmation modal on error
      }
    }
  }

  const handleSkip = () => {
    setConfirmationType('skip')
    setShowConfirmationModal(true)
  }

  const handleConfirmationConfirm = () => {
    setShowConfirmationModal(false)
    
    // Close the main modal after confirmation
    onClose()
    setConfirmationType(null)
  }

  const handleConfirmationClose = () => {
    setShowConfirmationModal(false)
    setConfirmationType(null)
  }

  const handleModalClose = (open) => {
    // onOpenChange receives a boolean - false means closing
    // onClose from BottomSheetDialog doesn't receive a parameter
    const isClosing = open === false || open === undefined
    
    if (isClosing) {
      // Intercept close action - show skip confirmation instead of closing directly
      // Only show confirmation if not already showing one
      if (!showConfirmationModal) {
        setConfirmationType('skip')
        setShowConfirmationModal(true)
      }
    }
    // If opening (open === true), do nothing - the modal is controlled by isOpen prop
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePageClick = (page) => {
    setCurrentPage(page)
  }

  // Generate page numbers to display with ellipsis
  const getPageNumbers = (currentPage, totalPages, adjacentPages) => {
    if (totalPages <= 1) return []
    
    const pages = new Set()
    
    // Always add first, current, and last
    pages.add(1)
    if (currentPage !== 1 && currentPage !== totalPages) {
      pages.add(currentPage)
    }
    pages.add(totalPages)
    
    // Add adjacent pages around current
    for (let i = Math.max(1, currentPage - adjacentPages); i <= Math.min(totalPages, currentPage + adjacentPages); i++) {
      pages.add(i)
    }
    
    // Convert to sorted array
    const sortedPages = Array.from(pages).sort((a, b) => a - b)
    
    // Build result with ellipsis
    const result = []
    for (let i = 0; i < sortedPages.length; i++) {
      if (i > 0 && sortedPages[i] - sortedPages[i - 1] > 1) {
        result.push('ellipsis')
      }
      result.push(sortedPages[i])
    }
    
    return result
  }

  const adjacentPages = isMobile ? 0 : 1
  const pageNumbers = getPageNumbers(currentPage, totalPages, adjacentPages)

  const footerContent = (
    <div className={`flex flex-col gap-4 p-4 md:p-6 ${isMobile ? '' : 'border-t border-gray-200'}`}>
      <div className="flex flex-row gap-4 w-full">
        <Button
          variant="outlinePrimary"
          className="flex-1 h-10 md:h-11"
          onClick={handleSkip}
        >
          Skip
        </Button>
        <Button
          className="flex-1 h-10 md:h-11"
          onClick={handleSubmit}
          disabled={selectedTransactionIds.length === 0}
        >
          Submit
        </Button>
      </div>
    </div>
  )

  // Pagination controls component
  const paginationControls = transactionsArray.length > 0 && totalPages > 1 ? (
    <div className={`flex flex-row items-center justify-between px-4 md:px-6 py-4 ${isMobile ? '' : 'border-t border-gray-200'} bg-white flex-shrink-0`}>
      <button
        onClick={handlePreviousPage}
        disabled={currentPage === 1}
        className={`flex items-center gap-2 ${
          currentPage === 1
            ? 'cursor-not-allowed text-neutral-400'
            : 'cursor-pointer hover:opacity-80 text-[--brand-500]'
        }`}
      >
        {!isMobile && <ArrowPrevious className={currentPage === 1 ? 'text-neutral-400' : 'text-[--brand-500]'} />}
        <span className={`text-[14px] font-medium ${
          currentPage === 1
            ? 'text-neutral-400'
            : 'text-[--brand-500]'
        }`}>Previous</span>
      </button>

      <div className="flex flex-row items-center gap-2">
        {pageNumbers.map((item, index) => {
          if (item === 'ellipsis') {
            return (
              <span key={`ellipsis-${index}`} className="text-neutral-700 px-1">
                ...
              </span>
            )
          }
          const page = item
          return (
            <button
              key={page}
              onClick={() => handlePageClick(page)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-[14px] font-normal focus:outline-none focus-visible:outline-none ${
                currentPage === page
                  ? 'bg-[--brand-500] text-white'
                  : 'text-neutral-700 hover:text-neutral-900 cursor-pointer'
              }`}
            >
              {page}
            </button>
          )
        })}
      </div>

      <button
        onClick={handleNextPage}
        disabled={currentPage === totalPages}
        className={`flex items-center gap-2 ${
          currentPage === totalPages
            ? 'cursor-not-allowed text-neutral-400'
            : 'cursor-pointer hover:opacity-80 text-[--brand-500]'
        }`}
      >
        <span className={`text-[14px] font-medium ${
          currentPage === totalPages
            ? 'text-neutral-400'
            : 'text-[--brand-500]'
        }`}>Next</span>
        {!isMobile && <ArrowNext className={currentPage === totalPages ? 'text-neutral-400' : 'text-[--brand-500]'} />}
      </button>
    </div>
  ) : null

  // Combined footer with pagination and buttons
  const combinedFooterContent = (
    <div className="flex flex-col">
      {paginationControls}
      {footerContent}
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <BottomSheetDialog
        onClose={handleModalClose}
        className="md:max-w-none md:w-[65vw] md:h-[85vh] md:max-h-[85vh] w-full h-[90vh] md:rounded-xl rounded-t-xl flex flex-col"
        overlayClassName="bg-black/80"
        footerContent={combinedFooterContent}
        closable={true}
      >
        <div className="flex flex-col flex-1 min-h-0">
          {/* Header */}
          <div className="flex flex-col gap-3 p-4 md:p-6 pb-4">
            <div className="flex flex-row items-start gap-4">
              <div className="flex items-center justify-center bg-gradient-to-r from-[--brand-850] to-[--brand-900] min-w-12 min-h-12 rounded-[10px] flex-shrink-0">
                <img src={CurrencyDollarCircleIcon} alt="Currency dollar circle" className="w-6 h-6 brightness-0 invert" />
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <h2 className="text-[18px] font-medium text-neutral-900">
                  Identify Your Loan or Lease Payment
                </h2>
                <p className="text-[14px] font-light text-neutral-700">
                  We couldn&apos;t automatically identify a loan or lease payment from your recent transactions. Please review the list below and select the transaction(s) that represent your loan or lease payment.
                </p>
              </div>
            </div>
          </div>

          {/* Content - Only scrollable area */}
          <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto">
              <PlaidTransactionPickerContent
                transactions={currentPageTransactions}
                selectedTransactionIds={selectedTransactionIds}
                onCheckboxChange={handleCheckboxChange}
                isLoading={false}
                error={null}
              />
            </div>
          </div>
        </div>
      </BottomSheetDialog>

      {/* Confirmation Modal */}
      {confirmationType && (
        <PlaidTransactionPickerConfirmationModal
          isOpen={showConfirmationModal}
          onClose={handleConfirmationClose}
          onCloseMainModal={onClose}
          type={confirmationType}
          onConfirm={handleConfirmationConfirm}
        />
      )}
    </Dialog>
  )
}

PlaidTransactionPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  plaidItemId: PropTypes.string.isRequired,
  transactions: PropTypes.array.isRequired,
}

export default PlaidTransactionPickerModal


